'use client'

import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { ErrorFallback } from '@/components/errors/errorFallback'
import { useSafeBack } from '@/hooks/use-safe-back'

/**
 * Factory for route-group `error.tsx` files (the feature tier). Next.js
 * replaces only the failing segment's children — the surrounding layout
 * (sidebar/header) keeps rendering, so "Go back" to a surface-appropriate
 * fallback is meaningful here in a way it isn't at the root tier.
 */
export function createFeatureErrorPage(fallbackHref: string) {
  return function FeatureErrorPage({
    error,
    reset,
  }: {
    error: Error & { digest?: string }
    reset: () => void
  }) {
    const { reset: resetQueries } = useQueryErrorResetBoundary()
    const router = useRouter()
    const goBack = useSafeBack(fallbackHref)

    return (
      <ErrorFallback
        tier="feature"
        error={error}
        reset={() => {
          resetQueries()
          reset()
          router.refresh()
        }}
        onBack={goBack}
      />
    )
  }
}
